import { ChevronRight, CheckCircle2 } from 'lucide-react';

function renderContent(content) {
  const lines = content.split('\n');
  const elements = [];
  let inTable = false;
  let tableRows = [];
  let tableHeaders = [];

  const processInlineFormatting = (text) => {
    // Bold
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>');
    // Code
    text = text.replace(/`(.+?)`/g, '<code class="text-xs bg-accent text-[#42CDD7] px-1.5 py-0.5 rounded font-[\'JetBrains_Mono\']">$1</code>');
    return text;
  };

  const flushTable = () => {
    if (tableHeaders.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="my-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted">
                {tableHeaders.map((h, i) => (
                  <th key={i} className="px-4 py-2.5 text-left text-xs font-semibold text-foreground/80 uppercase tracking-wider border-b border-border">
                    {h.trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-card' : 'bg-muted/50'}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-2.5 text-foreground/80 border-b border-border"
                      dangerouslySetInnerHTML={{ __html: processInlineFormatting(cell.trim()) }}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    tableHeaders = [];
    tableRows = [];
    inTable = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Table detection
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.split('|').filter(c => c.trim() !== '');
      if (!inTable) {
        inTable = true;
        tableHeaders = cells;
      } else if (cells.every(c => /^[\s-:]+$/.test(c))) {
        continue; // separator row
      } else {
        tableRows.push(cells);
      }
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Empty line
    if (!trimmed) {
      elements.push(<div key={i} className="h-3" />);
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(trimmed)) {
      const text = trimmed.replace(/^\d+\.\s/, '');
      elements.push(
        <div key={i} className="flex gap-3 items-start py-1.5">
          <span className="w-6 h-6 rounded-full bg-accent text-[#42CDD7] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
            {trimmed.match(/^(\d+)/)[1]}
          </span>
          <span className="text-sm text-foreground/80 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: processInlineFormatting(text) }}
          />
        </div>
      );
      continue;
    }

    // Bullet list
    if (trimmed.startsWith('- ')) {
      const text = trimmed.substring(2);
      elements.push(
        <div key={i} className="flex gap-2.5 items-start py-1 pl-1">
          <div className="w-1.5 h-1.5 rounded-full bg-[#42CDD7] shrink-0 mt-2" />
          <span className="text-sm text-foreground/80 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: processInlineFormatting(text) }}
          />
        </div>
      );
      continue;
    }

    // Paragraph
    elements.push(
      <p key={i} className="text-sm text-foreground/80 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: processInlineFormatting(trimmed) }}
      />
    );
  }

  if (inTable) flushTable();
  return elements;
}

export default function StepCard({ step, stepNumber, totalSteps, isActive, onClick, isCompleted = false, onMarkComplete }) {
  const TRAINING_BLUE = '#2563EB';
  return (
    <div
      data-testid={`step-card-${stepNumber}`}
      className={`step-animate border rounded-xl transition-all cursor-pointer ${
        isActive
          ? 'border-[#2563EB]/40 bg-card shadow-sm ring-1 ring-[#2563EB]/10'
          : isCompleted
            ? 'border-green-500/30 bg-card hover:shadow-sm'
            : 'border-border bg-card hover:border-slate-300 hover:shadow-sm'
      }`}
      onClick={onClick}
    >
      {/* Step header */}
      <div className="flex items-center gap-3 px-6 py-4">
        {isCompleted ? (
          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-green-500 text-white" data-testid={`step-completed-badge-${stepNumber}`}>
            <CheckCircle2 className="w-4 h-4" />
          </div>
        ) : (
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
            isActive ? 'text-white' : 'bg-muted text-muted-foreground'
          }`} style={isActive ? { backgroundColor: TRAINING_BLUE } : {}}>
            {stepNumber}
          </div>
        )}
        <h3 className="font-['Outfit'] text-base font-semibold flex-1 text-foreground">
          {step.title}
        </h3>
        <ChevronRight className="w-4 h-4 shrink-0 transition-transform" style={isActive ? { transform: 'rotate(90deg)', color: TRAINING_BLUE } : { color: 'hsl(var(--muted-foreground))' }} />
      </div>

      {/* Step content (expandable) */}
      {isActive && (
        <div className="px-6 pb-6 pt-0">
          <div className="border-t pt-4" style={{ borderColor: 'rgba(37,99,235,0.15)' }}>
            {renderContent(step.content)}
          </div>
          {onMarkComplete && (
            <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
              {isCompleted ? (
                <div className="flex items-center gap-2 text-sm text-green-600 font-medium" data-testid={`step-complete-badge-${stepNumber}`}>
                  <CheckCircle2 className="w-4 h-4" /> Step completed
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Done with this step? Mark it complete so your Practice Manager sees your progress.</p>
              )}
              {!isCompleted && (
                <button
                  data-testid={`step-complete-btn-${stepNumber}`}
                  onClick={(e) => { e.stopPropagation(); onMarkComplete(); }}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg transition-colors hover:opacity-90"
                  style={{ backgroundColor: '#16A34A' }}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Mark as Complete
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
